// SPLIT TIMES panel -> complete PNG table. The live panel can scroll in both
// directions, so redraw its derived values onto a content-sized canvas rather
// than screenshotting only the visible DOM viewport.

import { EXPORT_FONT, downloadCanvasPng, resolveBackground } from './chartExport.js';

const MIN_WIDTH = 1200;
const PAD = 32;
const LAP_W = 150;
const SECTOR_W = 140;
const TOTAL_W = 160;
const TITLE_H = 82;
const HEADER_H = 42;
const GROUP_H = 44;
const ROW_H = 36;
const STAT_H = 40;
const FOOTER_H = 42;

const BRIGHT = '#e8edf2';
const TEXT = '#c9d4dd';
const MUTED = '#8b98a5';
const BORDER = 'rgba(139,152,165,0.28)';
const GOLD = '#FFD700';
const BLUE = '#5599ff';
const RED = '#e8411a';

export function splitTimesExportLayout(sectorCount, lapCount, groupCount = 0, statusCount = 0) {
  const tableWidth = LAP_W + Math.max(0, sectorCount) * SECTOR_W + TOTAL_W;
  return {
    width: Math.max(MIN_WIDTH, PAD * 2 + tableWidth),
    height: TITLE_H + HEADER_H
      + Math.max(0, groupCount) * GROUP_H
      + (Math.max(0, lapCount) + Math.max(0, statusCount)) * ROW_H
      + 3 * STAT_H + FOOTER_H,
    tableWidth,
  };
}

export function formatSplitTime(seconds, alwaysMinutes = false) {
  if (seconds == null || !Number.isFinite(seconds)) return '—';
  if (alwaysMinutes || seconds >= 60) {
    const minutes = Math.floor(seconds / 60);
    const remainder = (seconds - minutes * 60).toFixed(3).padStart(6, '0');
    return `${minutes}:${remainder}`;
  }
  return seconds.toFixed(3);
}

function line(ctx, x1, y1, x2, y2) {
  ctx.strokeStyle = BORDER;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
}

function text(ctx, value, x, y, { color = TEXT, weight = 400, size = 16, align = 'center' } = {}) {
  ctx.fillStyle = color;
  ctx.font = `${weight} ${size}px ${EXPORT_FONT}`;
  ctx.textAlign = align;
  ctx.fillText(value, x, y);
}

/**
 * groups: [{ label, meta, accent, mismatch, loading, error,
 *            rows: [{ label, enabled, sectorTimes, totalLabel }] }]
 * stats: [{ label, sectorTimes, total, color }]
 */
export async function exportSplitTimesPng({
  title, subtitle, sectors, groups, stats, filename, watermark, themeFrom,
}) {
  const lapCount = groups.reduce((count, group) => count + group.rows.length, 0);
  const statusCount = groups.filter(group => group.loading || group.error).length;
  const layout = splitTimesExportLayout(sectors.length, lapCount, groups.length, statusCount);
  const canvas = document.createElement('canvas');
  canvas.width = layout.width;
  canvas.height = layout.height;
  const ctx = canvas.getContext('2d');
  const bg = resolveBackground(themeFrom);

  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.textBaseline = 'middle';

  text(ctx, title || 'Split Times', PAD, 27, { color: BRIGHT, weight: 600, size: 24, align: 'left' });
  if (subtitle) text(ctx, subtitle, PAD, 54, { color: MUTED, size: 14, align: 'left' });

  const tableLeft = (canvas.width - layout.tableWidth) / 2;
  const tableRight = tableLeft + layout.tableWidth;
  const colCenters = [tableLeft + LAP_W / 2];
  for (let i = 0; i < sectors.length; i += 1) {
    colCenters.push(tableLeft + LAP_W + i * SECTOR_W + SECTOR_W / 2);
  }
  colCenters.push(tableRight - TOTAL_W / 2);

  let y = TITLE_H;
  ctx.fillStyle = 'rgba(139,152,165,0.08)';
  ctx.fillRect(tableLeft, y, layout.tableWidth, HEADER_H);
  text(ctx, 'LAP', tableLeft + 14, y + HEADER_H / 2, { color: MUTED, weight: 600, size: 13, align: 'left' });
  sectors.forEach((sector, index) => {
    text(ctx, sector.label, colCenters[index + 1], y + HEADER_H / 2, { color: MUTED, weight: 600, size: 13 });
  });
  text(ctx, 'TOTAL', colCenters[colCenters.length - 1], y + HEADER_H / 2, { color: MUTED, weight: 600, size: 13 });
  line(ctx, tableLeft, y + HEADER_H, tableRight, y + HEADER_H);
  y += HEADER_H;

  let rowIndex = 0;
  groups.forEach(group => {
    const groupCenterY = y + GROUP_H / 2;
    ctx.fillStyle = 'rgba(139,152,165,0.10)';
    ctx.fillRect(tableLeft, y, layout.tableWidth, GROUP_H);
    ctx.fillStyle = group.accent || MUTED;
    ctx.fillRect(tableLeft + 14, groupCenterY - 6, 12, 12);
    text(ctx, group.label || 'Driver', tableLeft + 38, groupCenterY, {
      color: BRIGHT, weight: 700, size: 16, align: 'left',
    });
    const labelWidth = ctx.measureText(group.label || 'Driver').width;
    if (group.meta) {
      text(ctx, group.meta, tableLeft + 50 + labelWidth, groupCenterY, {
        color: MUTED, size: 14, align: 'left',
      });
    }
    if (group.mismatch) {
      text(ctx, 'S/F MISMATCH · EXCLUDED UNTIL RE-ALIGNED', tableRight - 14, groupCenterY, {
        color: GOLD, weight: 600, size: 13, align: 'right',
      });
    }
    line(ctx, tableLeft, y + GROUP_H, tableRight, y + GROUP_H);
    y += GROUP_H;

    if (group.loading || group.error) {
      text(ctx, group.loading ? 'Loading split times…' : group.error, tableLeft + 14, y + ROW_H / 2, {
        color: group.error ? RED : MUTED, size: 15, align: 'left',
      });
      line(ctx, tableLeft, y + ROW_H, tableRight, y + ROW_H);
      y += ROW_H;
    }

    group.rows.forEach(row => {
      const centerY = y + ROW_H / 2;
      const alpha = row.enabled ? 1 : 0.3;
      if (rowIndex % 2 === 1) {
        ctx.fillStyle = `rgba(139,152,165,${0.035 * alpha})`;
        ctx.fillRect(tableLeft, y, layout.tableWidth, ROW_H);
      }
      ctx.globalAlpha = alpha;
      text(ctx, `${row.enabled ? '✓' : '○'}  ${row.label}`, tableLeft + 14, centerY, {
        color: MUTED, weight: 600, size: 15, align: 'left',
      });
      row.sectorTimes.forEach((value, index) => {
        const isBest = row.enabled && value != null && stats[0]?.sectorTimes[index] != null
          && Math.abs(value - stats[0].sectorTimes[index]) < 0.001;
        text(ctx, formatSplitTime(value), colCenters[index + 1], centerY, {
          color: isBest ? GOLD : TEXT, weight: isBest ? 700 : 400,
        });
      });
      text(ctx, row.totalLabel || '—', colCenters[colCenters.length - 1], centerY, { color: TEXT });
      ctx.globalAlpha = 1;
      line(ctx, tableLeft, y + ROW_H, tableRight, y + ROW_H);
      y += ROW_H;
      rowIndex += 1;
    });
  });

  stats.forEach((stat) => {
    const centerY = y + STAT_H / 2;
    ctx.fillStyle = `${stat.background || 'rgba(139,152,165,0.06)'}`;
    ctx.fillRect(tableLeft, y, layout.tableWidth, STAT_H);
    text(ctx, stat.label.toUpperCase(), tableLeft + 14, centerY, {
      color: stat.color, weight: 700, size: 15, align: 'left',
    });
    stat.sectorTimes.forEach((value, index) => {
      text(ctx, formatSplitTime(value), colCenters[index + 1], centerY, {
        color: stat.color, weight: stat.label === 'Best' ? 700 : 600,
      });
    });
    text(ctx, formatSplitTime(stat.total, true), colCenters[colCenters.length - 1], centerY, {
      color: stat.color, weight: stat.label === 'Best' ? 700 : 600,
    });
    line(ctx, tableLeft, y + STAT_H, tableRight, y + STAT_H);
    y += STAT_H;
  });

  if (watermark) {
    text(ctx, watermark, canvas.width - PAD, canvas.height - 17, {
      color: '#5a6672', size: 14, align: 'right',
    });
  }

  await downloadCanvasPng(canvas, filename);
}

export const SPLIT_TIMES_STAT_COLORS = {
  best: { color: GOLD, background: 'rgba(255,215,0,0.07)' },
  avg: { color: BLUE, background: 'rgba(85,153,255,0.07)' },
  max: { color: RED, background: 'rgba(232,65,26,0.07)' },
};
